import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { TIER_CURRENCY, TIER_TRIAL_DAYS, TIER_TRIAL_SUBSCRIBERS } from "../domain/tiers";

/**
 * The billing-and-trial statement, in ONE place.
 *
 * 🔑 It is a component rather than a sentence repeated on each page, because
 * it had already gone wrong that way: the landing page, the pricing page and
 * the registration disclosure each carried their own copy, and an edit to one
 * of them left the registration screen reading "Your first the first 3 months
 * are free". Three copies of a commercial promise is three chances to state a
 * different one.
 *
 * The numbers come from domain/tiers, so the trial length and the currency
 * cannot drift from what the Terms say and what the invoice will do.
 */
export function TrialHighlight({
  variant = "banner",
}: {
  /** "banner" is the full-width prominent form; "inline" is a compact line. */
  variant?: "banner" | "inline";
}) {
  const days = TIER_TRIAL_DAYS;

  if (variant === "inline") {
    return (
      <Typography variant="body2" color="text.secondary">
        Billed monthly, in {TIER_CURRENCY}.{" "}
        <Box component="strong" sx={{ color: "success.main", fontWeight: 700 }}>
          Your first {days} days are free, on every plan.
        </Box>
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        // Deliberately not an Alert. Alerts on these pages carry warnings and
        // tax caveats, and this is the opposite kind of message -- reusing the
        // same visual language would file the best news on the page under the
        // same heading as the disclaimers.
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "success.main",
        background: (t) =>
          `linear-gradient(135deg, ${t.palette.success.main}14, ${t.palette.success.main}05)`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <AutoAwesomeIcon sx={{ color: "success.main", mt: 0.25 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, lineHeight: 1.3, color: "success.main" }}
          >
            Your first {days} days are free — on every plan
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Billed monthly, in {TIER_CURRENCY}. No payment instrument is required to start, and
            the free period applies to every plan, not just the entry one, with the full{" "}
            {TIER_TRIAL_SUBSCRIBERS.toLocaleString("en-IN")}-subscriber allowance — you need your
            real list loaded to learn anything from a trial.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}
